/**
 * Pillar F.4 — thread-aware body rendering: a CONSERVATIVE, DISPLAY-ONLY splitter that cuts a
 * plain-text email body at quoted-history boundaries so the latest message can render expanded and
 * the earlier ones collapse behind "Show earlier messages (N)".
 *
 * Three properties this module is built around, in priority order:
 *
 *  1. **Nothing is ever lost or reordered.** Every segment is a verbatim SLICE of the input taken by
 *     index, and the slices tile the whole string in order — so `segments.map(s => s.text).join('')`
 *     is byte-identical to the input. That is asserted as a property test, not as a hope, and it is
 *     why the segment text is never rebuilt from parsed pieces.
 *  2. **When unsure, do not split.** A body with no confident boundary comes back as ONE segment,
 *     which renders exactly as the body renders today. Any unexpected throw returns the same single
 *     segment (`splitEmailThread` cannot fail the render).
 *  3. **Stored content never changes.** This runs on whatever text `/email/{id}/body/content` already
 *     returns; nothing here writes, normalizes, or re-encodes.
 *
 * What counts as a boundary (deliberately narrow — three shapes, nothing else):
 *
 *  - a `-----Original Message-----` / `-----Forwarded Message-----` separator rule;
 *  - an `On <date>, <name> wrote:` attribution line — required to END in `wrote:` AND to contain a
 *    digit or an address, so ordinary prose ("On reflection, he wrote:") is not a boundary;
 *  - a HEADER BLOCK: two or more CONSECUTIVE `Key: value` lines, one of which must be `From:`. The
 *    run requirement is the whole defence against a false positive: a single line that happens to
 *    start "From:" or "To:" mid-sentence is not a boundary, and a signature block
 *    (`T: +1 212 555 0100`) uses keys that are not mail headers at all.
 *
 * Outlook quoting (`> ` prefixes) is tolerated on all three shapes.
 */

/** One rendered segment of a thread — newest first. */
export interface EmailSegment {
  /** Verbatim slice of the original body. The slices tile the input exactly. */
  text: string;
  /** The boundary line that introduced this segment, verbatim; null for the latest message. */
  headerLine: string | null;
  /** Best-effort sender for the collapsed label; null when it cannot be read confidently. */
  sender: string | null;
  /** Best-effort date for the collapsed label; null when it cannot be read confidently. */
  date: string | null;
}

/** Mail headers that may take part in a header-block boundary. NOT a signature's `T:` / `M:` / `E:`. */
const HEADER_KEYS = ['from', 'sent', 'to', 'cc', 'bcc', 'subject', 'date', 'reply-to', 'importance'];
const HEADER_LINE = new RegExp(`^\\s*>*\\s*(${HEADER_KEYS.join('|')})\\s*:\\s`, 'i');
/** A header line with an EMPTY value still counts inside a run (`Cc:` on its own is common). */
const HEADER_LINE_EMPTY = new RegExp(`^\\s*>*\\s*(${HEADER_KEYS.join('|')})\\s*:\\s*$`, 'i');
const FROM_LINE = /^\s*>*\s*from\s*:/i;
const SEPARATOR = /^\s*>*\s*-{3,}\s*(original message|forwarded message|original nachricht)\s*-{3,}\s*$/i;
const WROTE_LINE = /^\s*>*\s*on\b.*\bwrote:\s*$/i;

/** Beyond this many boundaries the tail is left in one block — a pathological body must not render
 *  hundreds of collapsed cards. Still lossless: the surplus text lands in the last segment. */
const MAX_SEGMENTS = 40;

/** Header lines are only a boundary as a RUN — a run this long or longer, containing a `From:`. */
const MIN_HEADER_RUN = 2;

/**
 * Distinct recognized header keys a run must carry to CUT on its own (review minor 4).
 *
 * ⚠ Two header-ish lines are not enough, even with a `From:` among them: the reviewer's probes
 * (`From: Dana Banker… / Sent: Monday…`, and `From: the seller / To: the buyer`) are ordinary prose
 * that satisfies the run test and collapsed an author's own answer behind "Show earlier messages".
 * Three distinct keys is the threshold because every real client writes at least From + Sent/Date +
 * To/Subject, while prose almost never lines up three of them consecutively.
 *
 * The review offered "a blank line before the run OR ≥3 keys"; this takes only the second half,
 * deliberately. A blank line above a 2-key run does not disambiguate the probes at all (quoted prose
 * is routinely set off by one), so the OR would still admit them. Requiring the keys unconditionally
 * is strictly stricter, and the cost of being wrong is nil: an unsplit body is exactly today's
 * rendering, which is the ruling's own fallback.
 */
const MIN_HEADER_RUN_KEYS = 3;

function isHeaderish(line: string): boolean {
  return HEADER_LINE.test(line) || HEADER_LINE_EMPTY.test(line);
}

const isBlank = (line: string) => line.trim() === '';

/** The recognized header key on a line, or null. */
function headerKeyOf(line: string): string | null {
  const key = /^\s*>*\s*([a-z-]+)\s*:/i.exec(line)?.[1]?.toLowerCase();
  return key != null && HEADER_KEYS.includes(key) ? key : null;
}

/** Length of the consecutive header run starting at `i`, or 0 when there is no qualifying run. */
function headerRunAt(lines: string[], i: number): number {
  let n = 0;
  while (i + n < lines.length && isHeaderish(lines[i + n])) n += 1;
  if (n < MIN_HEADER_RUN) return 0;
  // The run must actually look like a message's headers, not two coincidental colons.
  const hasFrom = lines.slice(i, i + n).some((l) => FROM_LINE.test(l));
  return hasFrom ? n : 0;
}

/** How many distinct recognized header keys the run at `i` (length `n`) carries. */
function headerRunKeyCount(lines: string[], i: number, n: number): number {
  const keys = new Set<string>();
  for (let k = i; k < i + n; k += 1) {
    const key = headerKeyOf(lines[k]);
    if (key) keys.add(key);
  }
  return keys.size;
}

/**
 * Whether only blank lines separate the previous boundary from line `i` (review minor 3).
 *
 * This is the absorb test: a separator rule is ALWAYS followed by its own header block, sometimes
 * several blank lines later, and that block is part of the same cut rather than a second one — the
 * spurious empty middle segment the reviewer saw on a loosely-spaced `-----Original Message-----`.
 *
 * ⚠ It is deliberately "zero non-blank lines", not "few non-blank lines". A tolerance would absorb a
 * genuine three-deep chain — `On … wrote:` / blank / `Second.` / blank / `From:` … — merging two
 * distinct messages into one segment. Blank-insensitive proximity is the fix; distance is not.
 */
function onlyBlankLinesBetween(lines: string[], from: number, to: number): boolean {
  for (let k = from + 1; k < to; k += 1) {
    if (!isBlank(lines[k])) return false;
  }
  return true;
}

function headerValue(line: string): string {
  const idx = line.indexOf(':');
  return idx === -1 ? '' : line.slice(idx + 1).trim();
}

/** Read sender/date out of the header run that follows a separator or starts a header block. */
function fromHeaderBlock(lines: string[], start: number): { sender: string | null; date: string | null } {
  let sender: string | null = null;
  let date: string | null = null;
  for (let i = start; i < Math.min(lines.length, start + 8); i += 1) {
    const line = lines[i];
    if (!isHeaderish(line)) continue;
    const key = /^\s*>*\s*([a-z-]+)\s*:/i.exec(line)?.[1]?.toLowerCase();
    if (key === 'from' && sender == null) sender = headerValue(line) || null;
    if ((key === 'sent' || key === 'date') && date == null) date = headerValue(line) || null;
  }
  return { sender, date };
}

/**
 * The label for an `On <date>, <name> <addr> wrote:` boundary — the line itself, minus the trailing
 * `wrote:`.
 *
 * ⚠ Deliberately NOT parsed into sender + date. Attribution lines interleave locale date formats,
 * name orders and address forms in ways no small regex gets right ("On Tue, Jul 1, 2026 at 9:03 AM
 * Jane Doe <jane@x.com>" has no comma between the date and the name; the Outlook form does), and
 * every attempt produced a label WORSE than the author's own words — a bare address where a full
 * "date · name" line was already sitting there. Header BLOCKS are where `From:`/`Sent:` make the two
 * fields unambiguous, so that is the only place this module claims to read them.
 */
function wroteLineLabel(line: string): string {
  return line.replace(/\s*\bwrote:\s*$/i, '').replace(/^\s*>*\s*/, '').trim();
}

interface Boundary {
  /** Character offset in the original body where this segment starts. */
  offset: number;
  line: number;
  sender: string | null;
  date: string | null;
  headerLine: string;
}

/**
 * Split a plain-text body into thread segments, NEWEST FIRST.
 *
 * Returns a single segment (the whole body, `headerLine: null`) whenever no confident boundary is
 * found — which is the "when unsure, show the whole body unsplit" ruling, and also what makes this
 * safe to call unconditionally.
 */
export function splitEmailThread(body: string): EmailSegment[] {
  const whole: EmailSegment[] = [{ text: body, headerLine: null, sender: null, date: null }];
  if (typeof body !== 'string' || body === '') return whole;
  try {
    // Keep the line terminators attached so offsets are exact and nothing is lost at a join.
    const lines = body.split('\n').map((l, i, arr) => (i < arr.length - 1 ? `${l}\n` : l));
    const offsets: number[] = [];
    let acc = 0;
    for (const l of lines) {
      offsets.push(acc);
      acc += l.length;
    }

    const boundaries: Boundary[] = [];
    let i = 0;
    while (i < lines.length) {
      if (boundaries.length >= MAX_SEGMENTS) break;
      const raw = lines[i];
      const line = raw.replace(/\r?\n$/, '');
      if (SEPARATOR.test(line)) {
        boundaries.push({ offset: offsets[i], line: i, headerLine: line.trim(),
                          ...fromHeaderBlock(lines, i + 1) });
        i += 1;
        continue;
      }
      // The digit/address requirement is what keeps ordinary prose out: "On reflection, he wrote:"
      // matches the shape but carries neither a date nor an address, so it is not a boundary.
      if (WROTE_LINE.test(line) && /[\d@<]/.test(line)) {
        boundaries.push({ offset: offsets[i], line: i, headerLine: wroteLineLabel(line),
                          sender: null, date: null });
        i += 1;
        continue;
      }
      const run = headerRunAt(lines, i);
      if (run > 0) {
        const prev = boundaries[boundaries.length - 1];
        // A header block separated from the previous boundary by nothing but blank lines IS that
        // boundary's own header block, not a second cut — otherwise every forwarded message splits
        // twice and a loosely-spaced separator leaves an empty segment between the two.
        if (prev != null && onlyBlankLinesBetween(lines, prev.line, i)) {
          i += run;
          continue;
        }
        // Not enough distinct header keys to be sure this is a quoted message rather than prose that
        // happens to start two lines with header words → do not cut (fail open).
        if (headerRunKeyCount(lines, i, run) < MIN_HEADER_RUN_KEYS) {
          i += run;
          continue;
        }
        // Label the cut with the run's `From:` line, which may not be the run's FIRST line — Outlook
        // emits `Subject:` first often enough that using lines[i] blindly labelled the segment with a
        // subject where a sender was wanted (review minor 5).
        const fromLine = lines.slice(i, i + run).find((l) => FROM_LINE.test(l)) ?? lines[i];
        boundaries.push({ offset: offsets[i], line: i,
                          headerLine: fromLine.replace(/\r?\n$/, '').trim(),
                          ...fromHeaderBlock(lines, i) });
        i += run;
        continue;
      }
      i += 1;
    }

    if (boundaries.length === 0) return whole;

    const segments: EmailSegment[] = [];
    const push = (start: number, end: number, b: Boundary | null) => {
      const text = body.slice(start, end);
      if (text === '') return; // a boundary at offset 0 leaves no latest-message slice
      segments.push({ text, headerLine: b?.headerLine ?? null, sender: b?.sender ?? null,
                      date: b?.date ?? null });
    };
    push(0, boundaries[0].offset, null);
    boundaries.forEach((b, n) => {
      const end = n + 1 < boundaries.length ? boundaries[n + 1].offset : body.length;
      push(b.offset, end, b);
    });
    // Nothing to gain from a "split" that produced one piece — fall back to the unsplit body so the
    // caller never renders a collapse control with nothing behind it.
    return segments.length >= 2 ? segments : whole;
  } catch {
    // Fail OPEN, always: a parser defect must degrade to today's rendering, never to a blank body.
    return whole;
  }
}

/** The collapsed row's label for a segment — the parsed sender/date when both read, else the
 *  boundary line verbatim (which is always correct: it is the author's own text). */
export function segmentLabel(seg: EmailSegment): string {
  const parts = [seg.sender, seg.date].filter((p): p is string => !!p && p.trim() !== '');
  if (parts.length > 0) return parts.join(' · ');
  return (seg.headerLine ?? '').trim() || 'Earlier message';
}
